<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
                <div v-if="limitInfo && limitInfo.maxLineConfigs !== null" class="limit-info" :class="{ 'limit-reached': !limitInfo.canCreate }">
                  <i class="ri-file-list-3-line"></i>
                  <span class="limit-text">
                    已使用 {{ limitInfo.currentCount }}{{ limitInfo.maxLineConfigs !== null ? ` / ${limitInfo.maxLineConfigs}` : '' }} 個 LINE 官方帳號
                  </span>
                  <span class="remaining" :class="{ 'limit-reached': !limitInfo.canCreate }">
                    {{ limitInfo.canCreate ? `（剩餘 ${limitInfo.maxLineConfigs - limitInfo.currentCount}）` : '（已達上限）' }}
                  </span>
                </div>
      </div>
      <div class="header-actions">
        <router-link 
                v-if="canCreateNewConfig"
                :to="{ name: getCreateRouteName() }" 
                class="btn btn-primary"
              >
                <i class="ri-add-line"></i>
                新增 LINE 設定
              </router-link>
        <button 
                v-else
                class="btn btn-primary" 
                disabled
                :title="limitInfoMessage"
              >
                <i class="ri-add-line"></i>
                新增 LINE 設定
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋設定名稱或 Channel ID..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterActive" class="form-control" @change="handleFilterChange">
          <option value="">全部</option>
          <option value="true">已啟用</option>
          <option value="false">未啟用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConfigs">重試</button>
    </div>

    <!-- 空狀態 -->
    <div v-else-if="configs.length === 0" class="empty-state-card">
      <i class="ri-line-fill empty-icon"></i>
      <p>沒有找到 LINE 設定</p>
    </div>

    <!-- 卡片格線 -->
    <div v-else class="configs-grid">
      <div
        v-for="config in configs"
        :key="config.id"
        class="line-card"
        :class="{ 'line-card--inactive': !config.isActive }"
      >
        <!-- 卡片頂部：頭像 + 名稱 + 狀態 -->
        <div class="line-card__header">
          <div class="line-card__avatar">
            <img
              v-if="config.botPictureUrl"
              :src="config.botPictureUrl"
              :alt="config.configName"
              class="avatar-img"
            />
            <div v-else class="avatar-placeholder">
              <i class="ri-line-fill"></i>
            </div>
          </div>
          <div class="line-card__title-area">
            <h3 class="line-card__name">{{ config.configName }}</h3>
            <span v-if="config.botBasicId || config.messagingApi?.botBasicId" class="line-card__basic-id">
              {{ config.botBasicId || config.messagingApi?.botBasicId }}
            </span>
          </div>
          <div class="line-card__status-badges">
            <span :class="config.isActive ? 'status-badge status-active' : 'status-badge status-inactive'">
              {{ config.isActive ? '啟用中' : '未啟用' }}
            </span>
            <!-- Webhook 狀態：已取得資料才顯示 -->
            <span
              v-if="config.webhookActive === true"
              class="status-badge status-webhook-on"
              title="Webhook 已啟用"
            ><i class="ri-webhook-line"></i> Webhook</span>
            <span
              v-else-if="config.webhookActive === false"
              class="status-badge status-webhook-off"
              title="Webhook 未啟用！收不到 LINE 訊息事件"
            ><i class="ri-alert-line"></i> Webhook 未啟用</span>
            <span
              v-if="config.webhookForwardEnabled"
              class="status-badge status-forward"
              title="Webhook 轉送已啟用"
            ><i class="ri-share-forward-line"></i> 轉送中</span>
            <span
              v-if="config.lastTestResult === 'success'" class="status-badge status-tested"
              title="連線測試成功"
            ><i class="ri-check-line"></i></span>
            <span
              v-else-if="config.lastTestResult === 'failed'" class="status-badge status-test-failed"
              title="連線測試失敗"
            ><i class="ri-close-line"></i></span>
          </div>
        </div>

        <!-- 統計區塊：第一行 好友數 + 封鎖 -->
        <div class="line-card__stats">
          <!-- 有效好友 -->
          <div class="stat-block">
            <div class="stat-label">
              <i class="ri-user-heart-line"></i> 有效好友
              <span v-if="config.followersCount == null && config.dbFollowers != null" class="source-tag">DB</span>
              <span v-else-if="config.followersCount != null" class="source-tag source-tag--line">LINE</span>
            </div>
            <div class="stat-value">
              {{ config.followersCount != null
                  ? config.followersCount.toLocaleString()
                  : config.dbFollowers != null
                    ? config.dbFollowers.toLocaleString()
                    : '—' }}
            </div>
            <div class="stat-sub">
              <span v-if="config.followersCount != null && config.followersCountUpdatedAt" class="updated-at" :title="'LINE API 更新時間：' + formatDate(config.followersCountUpdatedAt)">
                {{ formatFollowersUpdatedAt(config.followersCountUpdatedAt) }}
              </span>
              <span v-else-if="config.followersCount == null && config.dbFollowers != null" class="updated-at">資料庫統計</span>
              <span v-else class="updated-at">點擊同步</span>
            </div>
          </div>

          <!-- 封鎖 -->
          <div class="stat-block stat-block--last">
            <div class="stat-label">
              <i class="ri-user-unfollow-line"></i> 封鎖
              <span class="source-tag" :class="config.blockedCountSource === 'line' ? 'source-tag--line' : ''">
                {{ config.blockedCountSource === 'line' ? 'LINE' : 'DB' }}
              </span>
            </div>
            <div class="stat-value" :class="config.blockedCount > 0 ? 'text-danger' : ''">
              {{ config.blockedCount != null ? config.blockedCount.toLocaleString() : '0' }}
            </div>
            <div class="stat-sub">
              <span v-if="config.blockRate != null && config.blockRate > 0" class="block-rate-text">{{ config.blockRate }}%</span>
              <span v-else class="updated-at">—</span>
            </div>
          </div>
        </div>

        <!-- 統計區塊：第二行 本月訊息（獨立一行） -->
        <div class="line-card__quota">
          <div class="quota-row-label"><i class="ri-message-2-line"></i> 本月訊息</div>
          <div class="quota-row-content">
            <template v-if="config.messageQuotaType">
              <div class="quota-badges">
                <template v-if="inferPlanName(config.messageQuotaType, config.messageQuotaValue)">
                  <span class="plan-badge" :class="`plan-badge--${inferPlanName(config.messageQuotaType, config.messageQuotaValue).name === '高用量' ? 'high' : inferPlanName(config.messageQuotaType, config.messageQuotaValue).name === '中用量' ? 'mid' : inferPlanName(config.messageQuotaType, config.messageQuotaValue).name === '無上限' ? 'unlimited' : 'low'}`">
                    {{ inferPlanName(config.messageQuotaType, config.messageQuotaValue).name }}
                  </span>
                  <span v-if="inferPlanName(config.messageQuotaType, config.messageQuotaValue).addon" class="plan-badge plan-badge--addon">
                    +{{ inferPlanName(config.messageQuotaType, config.messageQuotaValue).addonCount.toLocaleString() }} 加購
                  </span>
                </template>
              </div>
              <!-- limited：進度條 -->
              <div v-if="config.messageQuotaType === 'limited'" class="quota-bar-wrap">
                <div class="quota-bar-track">
                  <div
                    class="quota-bar-fill"
                    :class="quotaUsagePct(config) >= 90 ? 'fill-danger' : quotaUsagePct(config) >= 70 ? 'fill-warning' : 'fill-success'"
                    :style="{ width: quotaUsagePct(config) + '%' }"
                  ></div>
                </div>
                <span class="quota-numbers">
                  <span :class="quotaUsagePct(config) >= 90 ? 'text-danger' : ''">
                    {{ (config.quotaConsumption ?? 0).toLocaleString() }}
                  </span>
                  <span class="quota-sep"> / {{ config.messageQuotaValue?.toLocaleString() }} 則</span>
                </span>
              </div>
              <!-- none：已用數 -->
              <span v-else-if="config.messageQuotaType === 'none' && config.quotaConsumption != null" class="quota-used-text">
                已用 {{ config.quotaConsumption.toLocaleString() }} 則
              </span>
            </template>
            <span v-else class="text-muted">尚未同步</span>
          </div>
        </div>

        <!-- Channel ID -->
        <div class="line-card__ids">
          <div class="id-row">
            <span class="id-label">Channel ID</span>
            <code class="id-value">{{ config.messagingApi?.channelId || '—' }}</code>
          </div>
          <div class="id-row">
            <span class="id-label">建立時間</span>
            <span class="id-value id-value--plain">{{ formatDate(config.createdAt) }}</span>
          </div>
          <div class="id-row">
            <span class="id-label">最後同步</span>
            <span class="id-value id-value--plain">
              <template v-if="config.webhookStatusUpdatedAt">
                {{ formatDate(config.webhookStatusUpdatedAt) }}
                <span
                  v-if="syncAgoText(config.webhookStatusUpdatedAt)"
                  class="sync-ago-badge"
                  :class="`sync-ago--${syncAgoText(config.webhookStatusUpdatedAt).level}`"
                >{{ syncAgoText(config.webhookStatusUpdatedAt).text }}</span>
              </template>
              <span v-else class="text-muted">尚未同步</span>
            </span>
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="line-card__actions">
          <!-- 第一排：功能性操作 -->
          <div class="action-row action-row--tools">
            <button
              class="action-btn action-btn--refresh"
              :class="{ spinning: refreshingStatsId === config.id }"
              :disabled="refreshingStatsId === config.id"
              @click="handleRefreshStats(config.id)"
              title="同步 LINE 統計資料"
            >
              <i class="ri-refresh-line"></i> 同步
            </button>
            <button
              class="action-btn action-btn--test"
              :disabled="testingConfigId === config.id"
              @click="handleTest(config.id)"
              title="測試連線"
            >
              <i class="ri-flashlight-line"></i> 測試
            </button>
            <button
              class="action-btn action-btn--forward"
              :class="{ 'action-btn--forward-active': config.webhookForwardEnabled }"
              @click="handleWebhookForward(config)"
              title="Webhook 轉送設定"
            >
              <i class="ri-share-forward-line"></i> 轉送
            </button>
            <button
              v-if="config.isActive"
              class="action-btn action-btn--toggle"
              @click="handleToggleStatus(config.id, false)"
            >
              <i class="ri-pause-circle-line"></i> 停用
            </button>
            <button
              v-else
              class="action-btn action-btn--activate"
              @click="handleToggleStatus(config.id, true)"
            >
              <i class="ri-play-circle-line"></i> 啟用
            </button>
          </div>
          <!-- 第二排：編輯 + 刪除 -->
          <div class="action-row action-row--primary">
            <router-link
              :to="{ name: getEditRouteName(), params: { id: config.id } }"
              class="action-btn action-btn--edit action-btn--wide"
            >
              <i class="ri-edit-line"></i> 編輯
            </router-link>
            <button
              class="action-btn action-btn--delete action-btn--wide"
              @click="handleDelete(config.id)"
            >
              <i class="ri-delete-bin-line"></i> 刪除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分頁 -->
    <ListPagination
      v-if="pagination.totalPages > 1"
      :page="currentPage"
      :total-pages="pagination.totalPages"
      :total="pagination.totalItems"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { lineService } from '@/api/modules/line.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'LineConfigList',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('LINE 官方帳號設定');
    const configs = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterActive = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const testingConfigId = ref(null);
    const refreshingStatsId = ref(null);
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });
    const limitInfo = ref(null);
    const canCreateNewConfig = computed(() => {
      if (!limitInfo.value) return true; // 如果沒有限制資訊（系統帳號），允許建立
      return limitInfo.value.canCreate;
    });
    const limitInfoMessage = computed(() => {
      if (!limitInfo.value || limitInfo.value.maxLineConfigs === null) {
        return '';
      }
      return `已達到 LINE 官方帳號數量上限（${limitInfo.value.currentCount}/${limitInfo.value.maxLineConfigs}）`;
    });

    const fetchConfigs = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterActive.value !== '') {
          params.isActive = filterActive.value === 'true';
        }

        const response = await lineService.getConfigs(params);

        if (response && response.success && response.data) {
          configs.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
          // 接收限制資訊
          if (response.data.limitInfo) {
            limitInfo.value = response.data.limitInfo;
          }
        } else {
          error.value = response?.message || '獲取 LINE 設定列表失敗';
        }
      } catch (err) {
        console.error('獲取 LINE 設定列表失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取 LINE 設定列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchConfigs();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchConfigs();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchConfigs();
      }
    };

    const handleTest = async (configId) => {
      testingConfigId.value = configId;
      try {
        const response = await lineService.testConnection(configId);
        if (response && response.success) {
          if (response.data.success) {
            await showSuccess('Webhook 測試成功', response.data.message || 'LINE Webhook 連線正常，可正常接收來自 LINE 的事件訊息。');
          } else {
            await showError('Webhook 測試失敗', response.data.message || 'LINE Webhook 連線測試失敗，請確認 Webhook URL 設定是否正確。');
          }
          await fetchConfigs();
        } else {
          await showError('測試失敗', response?.message || 'LINE 連線測試失敗');
        }
      } catch (err) {
        console.error('測試 LINE 連線失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '測試連線失敗';
        await showError('測試失敗', errorMessage);
      } finally {
        testingConfigId.value = null;
      }
    };

    const handleToggleStatus = async (configId, isActive) => {
      // 停用前先顯示影響說明
      if (!isActive) {
        const config = configs.value.find(c => c.id === configId);
        const name = config?.configName || '此 LINE 設定';
        const result = await Swal.fire({
          icon: 'warning',
          title: `確認停用「${name}」？`,
          html: `
            <div style="text-align:left; font-size:14px; line-height:1.8;">
              <p style="margin:0 0 12px; color:#555;">停用後將有以下影響：</p>
              <div style="margin-bottom:8px;">
                <span style="color:#e65100; font-weight:600;">⚠ 停止運作</span>
              </div>
              <ul style="margin:0 0 12px; padding-left:18px; color:#333;">
                <li><b>Webhook 停止接收</b>：加好友、取消追蹤、訊息、按鈕點擊等 LINE 事件將不再處理</li>
                <li><b>LIFF 綁定失效</b>：會員無法透過此帳號的 LIFF 頁面登入或綁定</li>
                <li><b>LINE 登入失效</b>：此帳號的 LINE 登入功能將停止</li>
              </ul>
              <div style="margin-bottom:8px;">
                <span style="color:#1b7e3e; font-weight:600;">✓ 仍可正常使用</span>
              </div>
              <ul style="margin:0 0 16px; padding-left:18px; color:#333;">
                <li><b>推播發送</b>：仍可對已加好友的會員推播訊息</li>
                <li><b>歷史資料保留</b>：好友記錄、推播記錄不受影響</li>
              </ul>
              ${config?.webhookActive === true ? `
              <div style="background:#fff8e1; border:1px solid #ffe082; border-radius:8px; padding:10px 14px; font-size:13px; color:#7a5a00;">
                <b>⚠ 注意：LINE Webhook 目前仍為啟用狀態</b><br>
                停用後系統不再處理 LINE 事件，但 LINE 平台仍會持續嘗試發送事件到伺服器。<br>
                若要完全關閉，請另外至 <b>LINE Developer Console → Messaging API → 關閉 Use webhook</b>。
              </div>
              ` : ''}
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '確認停用',
          cancelButtonText: '取消',
          confirmButtonColor: '#e65100',
          cancelButtonColor: '#6c757d',
          reverseButtons: true,
        });
        if (!result.isConfirmed) return;
      }

      try {
        const response = await lineService.updateStatus(configId, isActive);
        if (response && response.success) {
          await showSuccess('更新成功', isActive ? 'LINE 設定已啟用' : 'LINE 設定已停用');
          await fetchConfigs();
        } else {
          await showError('更新失敗', response?.message || '更新狀態失敗');
        }
      } catch (err) {
        console.error('更新狀態失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '更新狀態失敗';
        await showError('更新失敗', errorMessage);
      }
    };

    const handleDelete = async (configId) => {
      const config = configs.value.find(c => c.id === configId);
      const configName = config ? config.configName : '此設定';

      try {
      const confirmed = await showDeleteConfirm(configName, [
        'LINE 推播記錄',
        'LINE 訊息記錄',
        'LINE 訊息點擊記錄',
        'LINE 綁定記錄',
        'LINE Rich Menu',
        'LINE QR Code',
        'LINE 短網址',
        'LINE QR Code 掃描記錄',
        'LINE 短網址點擊記錄'
      ]);
        if (!confirmed) return;

        const response = await lineService.deleteConfig(configId);

        if (response && response.success) {
          await showSuccess('刪除成功', 'LINE 設定已刪除');
          await fetchConfigs();
        } else {
          await showError('刪除失敗', response?.message || '刪除 LINE 設定失敗');
        }
      } catch (err) {
        console.error('刪除 LINE 設定失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '刪除 LINE 設定失敗';
        await showError('刪除失敗', errorMessage);
      }
    };

    const handleRefreshStats = async (configId) => {
      const result = await Swal.fire({
        icon: 'info',
        title: '同步 LINE 統計資料',
        html: `
          <div style="text-align:left; font-size:14px; line-height:1.9;">
            <p style="margin:0 0 10px; color:#555;">同步後將從 LINE 官方 API 更新以下資料：</p>
              <ul style="margin:0 0 12px; padding-left:18px; color:#333;">
              <li><b>有效好友數</b>：昨日累計好友人數（LINE Insight API）</li>
              <li><b>封鎖人數</b>：昨日累計封鎖人數（LINE Insight API）</li>
              <li><b>訊息配額</b>：本月方案上限（含加購）</li>
              <li><b>本月已用數</b>：本月已發送的訊息則數</li>
            </ul>
            <p style="margin:0; color:#888; font-size:13px;">
              ⚠ 以上資料為 LINE 前一天的統計數字，非即時。<br>
              ⚠ 限已驗證的 LINE 官方帳號（Insight API 支援）。
            </p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '開始同步',
        cancelButtonText: '取消',
        confirmButtonColor: '#06c755',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;

      refreshingStatsId.value = configId;
      try {
        const response = await lineConfigApi.refreshStats(configId);
        if (response && response.success) {
          const config = configs.value.find(c => c.id === configId);
          if (config) {
            config.followersCount = response.data.followersCount;
            config.followersCountUpdatedAt = response.data.updatedAt;
            config.messageQuotaType = response.data.quotaType;
            config.messageQuotaValue = response.data.quotaValue;
            config.quotaConsumption = response.data.quotaConsumption;
            config.quotaRemaining = response.data.quotaRemaining;
            config.blockedCount = response.data.blockedCount;
            config.blockedCountSource = response.data.blockedCountSource;
            config.blockRate = response.data.blockRate;
            config.webhookActive = response.data.webhookActive;
            config.webhookStatusUpdatedAt = new Date().toISOString();
          }
        } else {
          await showError('更新失敗', response?.message || '無法取得統計資料，請確認帳號類型是否支援');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '更新失敗';
        await showError('更新失敗', msg);
      } finally {
        refreshingStatsId.value = null;
      }
    };

    const handleWebhookForward = async (config) => {
      const currentEnabled = config.webhookForwardEnabled || false;
      const currentUrl = config.webhookForwardUrl || '';
      const currentSecret = config.webhookForwardSecret || '';

      const { value: formValues } = await Swal.fire({
        title: 'Webhook 轉送設定',
        html: `
          <div style="text-align:left; font-size:14px; line-height:1.8;">
            <p style="margin:0 0 12px; color:#555; font-size:13px;">
              啟用後，系統收到 LINE 事件時會同步將原始 payload 轉發到您指定的 URL，
              適合需要自行串接 LINE 事件的開發者。
            </p>
            <div style="margin-bottom:14px;">
              <label style="display:flex; align-items:center; gap:8px; cursor:pointer; font-weight:600;">
                <input type="checkbox" id="swal-forward-enabled" ${currentEnabled ? 'checked' : ''} style="width:16px; height:16px; cursor:pointer;">
                啟用 Webhook 轉送
              </label>
            </div>
            <div id="swal-forward-fields" style="display:${currentEnabled ? 'block' : 'none'};">
              <div style="margin-bottom:12px;">
                <label style="display:block; margin-bottom:4px; font-weight:600;">轉送 URL <span style="color:#e53935;">*</span></label>
                <input id="swal-forward-url" type="url" value="${currentUrl}"
                  placeholder="https://your-server.com/webhook/line"
                  style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box;">
              </div>
              <div style="margin-bottom:4px;">
                <label style="display:block; margin-bottom:4px; font-weight:600;">簽章密鑰 <span style="color:#888; font-weight:400;">（選填）</span></label>
                <input id="swal-forward-secret" type="text" value="${currentSecret}"
                  placeholder="留空則不驗證簽章"
                  style="width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:6px; font-size:13px; box-sizing:border-box;">
                <p style="margin:6px 0 0; font-size:12px; color:#888;">設定後，轉送請求的 Header 會附帶 <code>X-Crm-Signature: sha256=...</code>，您可用此密鑰驗證來源。</p>
              </div>
            </div>
          </div>
        `,
        didOpen: () => {
          const checkbox = document.getElementById('swal-forward-enabled');
          const fields = document.getElementById('swal-forward-fields');
          checkbox.addEventListener('change', () => {
            fields.style.display = checkbox.checked ? 'block' : 'none';
          });
        },
        showCancelButton: true,
        confirmButtonText: '儲存',
        cancelButtonText: '取消',
        confirmButtonColor: '#1a73e8',
        preConfirm: () => {
          const enabled = document.getElementById('swal-forward-enabled').checked;
          const forwardUrl = document.getElementById('swal-forward-url')?.value?.trim() || '';
          const forwardSecret = document.getElementById('swal-forward-secret')?.value?.trim() || '';
          if (enabled) {
            if (!forwardUrl) {
              Swal.showValidationMessage('啟用轉送時必須填寫轉送 URL');
              return false;
            }
            try {
              const parsed = new URL(forwardUrl);
              if (parsed.protocol !== 'https:') {
                Swal.showValidationMessage('轉送 URL 必須使用 HTTPS（例如：https://your-server.com/webhook）');
                return false;
              }
            } catch {
              Swal.showValidationMessage('請輸入有效的網址格式（例如：https://your-server.com/webhook）');
              return false;
            }
          }
          return { enabled, forwardUrl: forwardUrl || null, forwardSecret: forwardSecret || null };
        }
      });

      if (!formValues) return;

      try {
        const response = await lineService.updateWebhookForward(config.id, formValues);
        if (response && response.success) {
          config.webhookForwardEnabled = formValues.enabled;
          config.webhookForwardUrl = formValues.forwardUrl;
          config.webhookForwardSecret = formValues.forwardSecret;
          await showSuccess('儲存成功', formValues.enabled ? 'Webhook 轉送已啟用' : 'Webhook 轉送已停用');
        } else {
          await showError('儲存失敗', response?.message || '設定儲存失敗');
        }
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message || '設定儲存失敗');
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const formatFollowersUpdatedAt = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH < 1) return '剛剛更新';
      if (diffH < 24) return `${diffH} 小時前更新`;
      return `${Math.floor(diffH / 24)} 天前更新`;
    };

    const syncAgoText = (dateString) => {
      if (!dateString) return null;
      const diffMs = Date.now() - new Date(dateString).getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffH    = Math.floor(diffMs / 3600000);
      const diffD    = Math.floor(diffMs / 86400000);
      if (diffMins < 1)  return { text: '剛剛', level: 'fresh' };
      if (diffMins < 60) return { text: `${diffMins} 分鐘前`, level: 'fresh' };
      if (diffH < 24)    return { text: `${diffH} 小時前`, level: 'ok' };
      if (diffD < 3)     return { text: `${diffD} 天前`, level: 'warn' };
      return               { text: `${diffD} 天前`, level: 'stale' };
    };

    const quotaUsagePct = (config) => {
      if (!config.messageQuotaValue) return 0;
      return Math.min(100, Math.round((config.quotaConsumption ?? 0) / config.messageQuotaValue * 100));
    };

    // 依台灣 LINE OA 方案訊息數推算方案名稱（高用量方案才可加購）
    const inferPlanName = (quotaType, quotaValue) => {
      if (quotaType === 'none') return { name: '無上限', addon: false };
      if (!quotaValue) return null;
      if (quotaValue <= 200)  return { name: '輕用量', addon: false };
      if (quotaValue <= 3000) return { name: '中用量', addon: false };
      if (quotaValue === 6000) return { name: '高用量', addon: false };
      if (quotaValue > 6000)  return { name: '高用量', addon: true, addonCount: quotaValue - 6000 };
      return null;
    };

    const getCreateRouteName = () => {
      return 'ClientCreateLineConfig';
    };

    const getEditRouteName = () => {
      return 'ClientEditLineConfig';
    };

    onMounted(() => {
      fetchConfigs();
    });

    return {
      pageTitle,
      configs,
      loading,
      error,
      searchQuery,
      filterActive,
      currentPage,
      pagination,
      testingConfigId,
      refreshingStatsId,
      fetchConfigs,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleTest,
      handleToggleStatus,
      handleDelete,
      handleRefreshStats,
      handleWebhookForward,
      syncAgoText,
      formatDate,
      formatFollowersUpdatedAt,
      quotaUsagePct,
      inferPlanName,
      getCreateRouteName,
      getEditRouteName,
      canCreateNewConfig,
      limitInfoMessage,
      limitInfo,
      route,
      router
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* ── 空狀態 ── */
.empty-state-card {
  text-align: center;
  padding: 60px 20px;
  color: #888;
}
.empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
  color: #bbb;
}

/* ── 卡片格線 ── */
.configs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

/* ── 單張卡片 ── */
.line-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
}
.line-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.11);
  transform: translateY(-2px);
}
.line-card--inactive {
  opacity: 0.75;
  border-color: #e0e0e0;
}
.line-card--inactive .line-card__header {
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  border-bottom-color: #e0e0e0;
}

/* ── 頂部：頭像 + 名稱 + 狀態 ── */
.line-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f0fdf4 0%, #e8f5e9 100%);
  border-bottom: 1px solid #d8f0dc;
}

.line-card__avatar {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0fdf4;
  border: 2px solid #06c755;
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-placeholder {
  font-size: 26px;
  color: #06c755;
  line-height: 1;
}

.line-card__title-area {
  flex: 1;
  min-width: 0;
}
.line-card__name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.line-card__basic-id {
  font-size: 13px;
  color: #06c755;
  font-weight: 500;
}

.line-card__status-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}
.status-active       { background: #e6f9ee; color: #059141; }
.status-inactive     { background: #eee; color: #555; }
.status-tested       { background: #e6f9ee; color: #1b7e3e; }
.status-test-failed  { background: #fdf0f0; color: #c0392b; }
.status-webhook-on   { background: #e8f4fd; color: #1565c0; }
.status-webhook-off  { background: #fff3cd; color: #856404; font-weight: 700; }
.status-forward      { background: #f3e8fd; color: #7b1fa2; }

/* ── 統計區：第一行 ── */
.line-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 16px 16px 12px;
  margin-top: 14px;
  border-top: 1px solid #f5f5f5;
}

.stat-block {
  padding: 0 12px;
  border-right: 1px solid #f0f0f0;
}
.stat-block:first-child { padding-left: 0; }
.stat-block--last { border-right: none; }

/* ── 本月訊息：第二行 ── */
.line-card__quota {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 16px 14px;
  border-top: 1px solid #f5f5f5;
  border-bottom: 1px solid #f5f5f5;
}
.quota-row-label {
  font-size: 15px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  padding-top: 2px;
  min-width: 72px;
}
.quota-row-content {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.source-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  background: #e4e4e4;
  color: #555;
  letter-spacing: 0.02em;
}
.source-tag--line {
  background: #d0f0de;
  color: #1b7e3e;
}
.stat-value {
  font-size: clamp(16px, 3vw, 22px);
  font-weight: 700;
  color: #1a1a1a;
  word-break: break-all;
  line-height: 1.2;
}
.stat-sub {
  margin-top: 3px;
}
.updated-at {
  font-size: 12px;
  color: #888;
}
.block-rate-text {
  font-size: 12px;
  color: #c0392b;
  background: #fdf0f0;
  border-radius: 3px;
  padding: 1px 6px;
}
.text-danger { color: #e74c3c; }

/* quota */
.quota-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.quota-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.plan-badge {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}
.plan-badge--high      { background: #e3f2fd; color: #1565c0; }
.plan-badge--mid       { background: #e0f7fa; color: #00695c; }
.plan-badge--unlimited { background: #e6f9ee; color: #1b7e3e; }
.plan-badge--low       { background: #eee; color: #444; }
.plan-badge--addon     { background: #fff3e0; color: #e65100; }

.quota-bar-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.quota-bar-track {
  flex: 1;
  max-width: 120px;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}
.quota-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.fill-success { background: #27ae60; }
.fill-warning { background: #f39c12; }
.fill-danger  { background: #e74c3c; }

.quota-numbers {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}
.quota-sep { color: #666; }
.quota-used-text { font-size: 13px; color: #444; }

/* ── ID 欄位 ── */
.line-card__ids {
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.id-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.id-label {
  color: #666;
  min-width: 80px;
  flex-shrink: 0;
}
.id-value {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  background: #f0f0f0;
  padding: 2px 7px;
  border-radius: 3px;
  color: #333;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sync-ago-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}
.sync-ago--fresh  { background: #e6f9ee; color: #1b7e3e; }
.sync-ago--ok     { background: #e8f4fd; color: #1565c0; }
.sync-ago--warn   { background: #fff3cd; color: #7a5a00; }
.sync-ago--stale  { background: #fdf0f0; color: #c0392b; }

.id-value--plain {
  font-family: inherit;
  background: none;
  padding: 0;
  color: #555;
  font-size: 13px;
}

/* ── 操作按鈕 ── */
.line-card__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}
.action-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.action-row--tools .action-btn {
  flex: 1;
  justify-content: center;
}
.action-row--primary {
  gap: 6px;
}
.action-btn--wide {
  flex: 1;
  justify-content: center;
  padding: 8px 12px;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}
.action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.action-btn--refresh  { background: #ebebeb; color: #333; border-color: #d4d4d4; }
.action-btn--refresh:hover:not(:disabled) { background: #e4e4e4; }
.action-btn--test     { background: #e8f4fd; color: #1565c0; border-color: #c9e4f7; }
.action-btn--test:hover:not(:disabled) { background: #d4ebfb; }
.action-btn--edit     { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; border-color: transparent; }
.action-btn--edit:hover { opacity: 0.9; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); }
.action-btn--toggle   { background: #fff8e1; color: #e65100; border-color: #ffe0b2; }
.action-btn--toggle:hover { background: #fff3cd; }
.action-btn--activate { background: #e6f9ee; color: #1b7e3e; border-color: #b2dfcb; }
.action-btn--activate:hover { background: #d0f0de; }
.action-btn--forward  { background: #f3e8fd; color: #7b1fa2; border-color: #e1c4f7; }
.action-btn--forward:hover { background: #ead4fc; }
.action-btn--forward-active { background: #7b1fa2; color: #fff; border-color: #7b1fa2; }
.action-btn--forward-active:hover { background: #6a1b9a; }
.action-btn--delete   { background: #dc3545; color: #fff; border-color: #dc3545; }
.action-btn--delete:hover { background: #c82333; border-color: #c82333; }

.action-btn--refresh.spinning i {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ── 分頁 ── */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px 0 8px;
}

.text-muted { color: #666; }
</style>
